import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { ApiError } from '@posilka/client/api/client';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { ListSkeleton } from './components/States';
import { AuthGate } from './features/auth/AuthGate';
import { HomePage } from './pages/HomePage';
import { MyOrdersPage } from './pages/MyOrdersPage';
import { NewOrderPage } from './pages/NewOrderPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OrderPage } from './pages/OrderPage';
import { ProfilePage } from './pages/ProfilePage';
import { RulesPage } from './pages/RulesPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { DriverProfilePage } from './pages/DriverProfilePage';

/** Shafyor va admin sahifalari alohida chunk — yuboruvchilarning birinchi yuklanishi yengil. */
const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) => {
  const Page = lazy<ComponentType>(() => load().then((m) => ({ default: m[name] })));
  return (
    <Suspense fallback={<ListSkeleton />}>
      <Page />
    </Suspense>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 4xx — qayta urinishdan foyda yo'q.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

// Dev: brauzer konsolidan kesh holatini ko'rish uchun.
if (import.meta.env.DEV) (window as unknown as { __qc: QueryClient }).__qc = queryClient;

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'orders', element: <MyOrdersPage /> },
      { path: 'orders/new', element: <NewOrderPage /> },
      { path: 'orders/:id', element: <OrderPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'rules', element: <RulesPage /> },
      { path: 'feedback', element: <FeedbackPage /> },
      { path: 'drivers/:id', element: <DriverProfilePage /> },
      {
        path: 'driver/apply',
        element: lazyPage(() => import('./pages/driver/DriverApplyPage'), 'DriverApplyPage'),
      },
      {
        path: 'driver/active',
        element: lazyPage(() => import('./pages/driver/DriverActivePage'), 'DriverActivePage'),
      },
      {
        path: 'driver/trips',
        element: lazyPage(() => import('./pages/driver/DriverTripsPage'), 'DriverTripsPage'),
      },
      {
        path: 'driver/routes',
        element: lazyPage(() => import('./pages/driver/DriverRoutesPage'), 'DriverRoutesPage'),
      },
      {
        path: 'driver/orders/:id/pickup',
        element: lazyPage(() => import('./pages/driver/PickupPage'), 'PickupPage'),
      },
      {
        path: 'driver/orders/:id/deliver',
        element: lazyPage(() => import('./pages/driver/DeliverPage'), 'DeliverPage'),
      },
      { path: 'admin', element: lazyPage(() => import('./pages/admin/AdminPage'), 'AdminPage') },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate>
          <RouterProvider router={router} />
        </AuthGate>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
